import React, { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setField as setRevenueAnalyticsField, updateSourceConfiguration } from 'store/redux/reducers/revenueAnalytics/revenueAnalyticsSlice';
import { dataModelType } from './utils/utils';
import { useDemoMode } from '../../../context/DemoModeContext';
import type { RootState } from 'store/redux/store';

import { CreditCard, Receipt, Code2, FileUp, Building2, Clock, FileText, Database, LayoutList, ChevronDown, CheckCircle, Pencil, AlertTriangle, Info } from 'lucide-react';
import { SiStripe, SiQuickbooks } from 'react-icons/si';
import ChargebeeIcon from '../../../assets/images/Chargebee_ideEpxhfdE_1.svg';
import { QuickBooksSetupForm } from './QuickBooksSetupForm';

export type DataSourceCategory = 'subscription' | 'transactions';

export interface SelectedSource {
  id: string;
  name: string;
  category: DataSourceCategory;
}

interface DataSourceDemoFlowProps {
  alreadyConfiguredSourceIds?: string[];
  forceReset?: boolean;
}

// ─── constants ───────────────────────────────────────────────────────────────

interface ConnectorTag {
  id: string;
  label: string;
  icon: React.ReactNode;
}

const SUBSCRIPTION_TAGS: ConnectorTag[] = [
  {
    id: 'chargebee',
    label: 'Chargebee',
    icon: <img src={ChargebeeIcon} alt="Chargebee" className="w-3.5 h-3.5 object-contain" />,
  },
  {
    id: 'stripe-billing',
    label: 'Stripe billing',
    icon: <SiStripe className="w-3.5 h-3.5 text-[#635BFF]" />,
  },
];

const TRANSACTION_TAGS: ConnectorTag[] = [
  {
    id: 'stripe',
    label: 'Stripe',
    icon: <SiStripe className="w-3.5 h-3.5 text-[#635BFF]" />,
  },
  {
    id: 'quickbooks',
    label: 'QuickBooks',
    icon: <SiQuickbooks className="w-3.5 h-3.5 text-[#2CA01C]" />,
  },
  {
    id: 'api',
    label: 'API',
    icon: <Code2 className="w-3.5 h-3.5 text-gray-500" />,
  },
  {
    id: 'csv',
    label: 'CSV upload',
    icon: <FileUp className="w-3.5 h-3.5 text-gray-500" />,
  },
];

const CATEGORIES = [
  {
    id: 'subscription' as DataSourceCategory,
    icon: CreditCard,
    iconBg: 'bg-blue-50',
    iconColor: 'text-blue-600',
    title: 'Subscription management',
    description: 'Your primary billing platform. Tracks customers, plans, billing cycles and subscription events.',
    tags: SUBSCRIPTION_TAGS,
  },
  {
    id: 'transactions' as DataSourceCategory,
    icon: Receipt,
    iconBg: 'bg-purple-50',
    iconColor: 'text-purple-600',
    title: 'Transactions & Invoices',
    description: 'Direct payments, marketplace charges, or revenue that flows outside your subscription system.',
    tags: TRANSACTION_TAGS,
  },
];

const SUBSCRIPTION_SOURCES = [
  {
    id: 'chargebee',
    name: 'Chargebee',
    description: 'Subscription and billing management platform',
    features: ['Auto-sync subscriptions', 'Customer data', 'Invoice tracking', 'Real-time updates'],
    logoType: 'chargebee' as const,
  },
  {
    id: 'stripe',
    name: 'Stripe',
    description: "Recurring billing via Stripe's subscription product",
    features: ['Auto-sync payments', 'Customer data', 'Subscription tracking', 'Real-time updates'],
    logoType: 'stripe' as const,
  },
];

const TRANSACTION_SOURCES = [
  {
    id: 'stripe',
    name: 'Stripe',
    description: 'Marketplace or direct charges outside subscription billing',
    features: ['Real-time sync', 'Webhook support', 'Direct charges', 'Marketplace support'],
    iconType: 'stripe' as const,
    comingSoon: false,
    beta: false,
    needsDataModel: false,
  },
  {
    id: 'quickbooks',
    name: 'QuickBooks',
    description: 'Sync invoices and transactions from QuickBooks Online',
    features: ['Invoice sync', 'Transaction import', 'OAuth 2.0', 'Sandbox support'],
    iconType: 'quickbooks' as const,
    comingSoon: false,
    beta: true,
    needsDataModel: false,
    needsSetup: false,
    demoOnly: false,
  },
  {
    id: 'api',
    name: 'API integration',
    description: 'REST API push — real-time or bulk',
    features: ['Real-time sync', 'Bulk operations', 'Webhook support', 'Authentication'],
    icon: Code2,
    iconType: 'lucide' as const,
    iconColor: 'text-blue-600',
    comingSoon: false,
    beta: false,
    needsDataModel: false,
    needsSetup: false,
  },
  {
    id: 'csv',
    name: 'Manual CSV upload',
    description: 'One-time or historical import',
    features: ['One-time import', 'Historical data', 'Bulk processing', 'Data validation'],
    icon: FileUp,
    iconType: 'lucide' as const,
    iconColor: 'text-green-600',
    comingSoon: false,
    beta: true,
    needsDataModel: false,
    needsSetup: false,
  },
  {
    id: 'salesforce',
    name: 'Salesforce',
    description: 'Import from your Salesforce CRM',
    features: ['CRM integration', 'Lead tracking', 'Opportunity sync', 'Custom fields'],
    icon: Building2,
    iconType: 'lucide' as const,
    iconColor: 'text-gray-400',
    comingSoon: true,
    beta: false,
    needsDataModel: false,
    needsSetup: false,
    demoOnly: false,
  },
];

const DATA_MODEL_OPTIONS = [
  {
    value: dataModelType.INVOICE_TRANSACTION,
    label: 'Transactions + Invoices',
    description: 'Individual payment records without the need for linked subscription IDs or subscription-level details.',
    entities: ['transactions', 'invoices'],
    icon: FileText,
    iconColor: 'text-blue-600',
    disabled: false,
  },
  {
    value: dataModelType.HIERARCHICAL,
    label: 'Complete Revenue Data',
    description: 'Complete entity relationships — customers, invoices, transactions, plans, and subscriptions.',
    entities: ['customers', 'invoices', 'transactions', 'plans', 'subscriptions'],
    icon: Database,
    iconColor: 'text-purple-600',
    disabled: true,
  },
  {
    value: dataModelType.PLAN_SUBSCRIPTION,
    label: 'Plan + Subscription',
    description: 'Subscription plans and customer subscriptions.',
    entities: ['plans', 'subscriptions'],
    icon: LayoutList,
    iconColor: 'text-green-600',
    disabled: true,
  },
];

// ─── helpers ─────────────────────────────────────────────────────────────────

const SelectedCheck = () => (
  <div className="absolute top-4 right-4 w-5 h-5 bg-primaryColor rounded-full flex items-center justify-center">
    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
    </svg>
  </div>
);

// ─── component ───────────────────────────────────────────────────────────────

export default function DataSourceDemoFlow({ alreadyConfiguredSourceIds = [], forceReset = false }: DataSourceDemoFlowProps) {
  const dispatch = useDispatch();
  const { isDemoMode, toggleDemoMode } = useDemoMode();

  const reduxSelectedSources = useSelector((s: RootState) => s.revenueAnalytics.selectedSources ?? []);
  const reduxSourceConfigurations = useSelector((s: RootState) => s.revenueAnalytics.sourceConfigurations ?? {});
  const reduxSelectedDataType = useSelector((s: RootState) => s.revenueAnalytics.selectedDataType);
  const reduxActiveSourceTab = useSelector((s: RootState) => s.revenueAnalytics.activeSourceTab);

  const restoredSourceId =
    !forceReset && reduxActiveSourceTab && reduxSelectedSources.includes(reduxActiveSourceTab)
      ? reduxActiveSourceTab
      : (!forceReset ? reduxSelectedSources[0] : undefined);

  // Restore stepper state from Redux on mount so "Back to Integrations → Configure" preserves progress.
  // When forceReset=true (adding a new connection), start blank regardless of Redux state.
  const [selectedCategory, setSelectedCategory] = useState<DataSourceCategory | null>(() => {
    if (forceReset || !restoredSourceId) return null;
    const sourceId = restoredSourceId;
    return (reduxSourceConfigurations[sourceId]?.sourceCategory as DataSourceCategory) ?? null;
  });

  const [selectedSource, setSelectedSource] = useState<SelectedSource | null>(() => {
    if (forceReset || !restoredSourceId) return null;
    const sourceId = restoredSourceId;
    const category = (reduxSourceConfigurations[sourceId]?.sourceCategory as DataSourceCategory) ?? null;
    if (!category) return null;
    const allSources = [...SUBSCRIPTION_SOURCES, ...TRANSACTION_SOURCES];
    const found = allSources.find(s => s.id === sourceId);
    return found ? { id: found.id, name: found.name, category } : null;
  });

  const [selectedDataModel, setSelectedDataModel] = useState<string>(() => {
    if (forceReset) return dataModelType.INVOICE_TRANSACTION;
    return reduxSelectedDataType || dataModelType.INVOICE_TRANSACTION;
  });

  const [activeStep, setActiveStep] = useState<number>(() => {
    if (forceReset || !restoredSourceId) return 1;
    const sourceId = restoredSourceId;
    const category = reduxSourceConfigurations[sourceId]?.sourceCategory;
    if (!category) return 1;
    const allSources = [...SUBSCRIPTION_SOURCES, ...TRANSACTION_SOURCES];
    const found = allSources.find(s => s.id === sourceId);
    const needsDataModel = TRANSACTION_SOURCES.find(s => s.id === sourceId)?.needsDataModel ?? false;
    if (found && needsDataModel && reduxSelectedDataType) return 3;
    if (found) return 2;
    return 1;
  });


  const SUBSCRIPTION_SOURCE_IDS = ['chargebee', 'stripe'];
  const hasSubscriptionConfigured = SUBSCRIPTION_SOURCE_IDS.some(id => alreadyConfiguredSourceIds.includes(id));

  const sourcesForCategory = selectedCategory === 'subscription'
    ? SUBSCRIPTION_SOURCES
    : TRANSACTION_SOURCES.filter(s => !s.demoOnly || isDemoMode);

  const selectedSourceDef = selectedSource
    ? TRANSACTION_SOURCES.find(s => s.id === selectedSource.id)
    : null;
  const selectedSourceNeedsDataModel = selectedSourceDef?.needsDataModel ?? false;
  const selectedSourceNeedsSetup = selectedSourceDef?.needsSetup ?? false;
  const selectedSourceNeedsStep3 = selectedSourceNeedsDataModel || selectedSourceNeedsSetup;

  const totalSteps = selectedSourceNeedsStep3 ? 3 : 2;

  const commitSelection = useCallback((source: SelectedSource, dataModel: string) => {
    // Append to existing sources so adding a second integration doesn't remove the first
    const merged = Array.from(new Set([...reduxSelectedSources, source.id]));
    dispatch(setRevenueAnalyticsField({ key: 'selectedSources', value: merged }));
    dispatch(setRevenueAnalyticsField({ key: 'selectedDataType', value: dataModel }));
    dispatch(setRevenueAnalyticsField({ key: 'activeSourceTab', value: source.id }));
    if (source.id === 'chargebee') dispatch(setRevenueAnalyticsField({ key: 'selectedProvider', value: 'chargebee' }));
    if (source.id === 'stripe') dispatch(setRevenueAnalyticsField({ key: 'selectedProvider', value: 'stripe' }));
    dispatch(updateSourceConfiguration({ sourceId: source.id, config: { sourceCategory: source.category } }));
  }, [dispatch, reduxSelectedSources]);

  // ── step handlers ──────────────────────────────────────────────────────────

  const handleCategorySelect = (cat: DataSourceCategory) => {
    if (selectedCategory !== cat) {
      setSelectedSource(null);
      setSelectedDataModel(dataModelType.INVOICE_TRANSACTION);
    }
    setSelectedCategory(cat);
  };

  const handleCategoryNext = () => {
    if (!selectedCategory) return;
    setActiveStep(2);
  };


  const handleSourceSelect = (source: SelectedSource) => {
    setSelectedSource(source);
    const srcDef = TRANSACTION_SOURCES.find(s => s.id === source.id);
    if (!srcDef?.needsDataModel && !srcDef?.needsSetup) {
      commitSelection(source, dataModelType.INVOICE_TRANSACTION);
    }
  };

  const handleSourceNext = () => {
    if (!selectedSource) return;
    if (selectedSourceNeedsStep3) {
      setActiveStep(3);
    }
  };

  // ── stepper step ───────────────────────────────────────────────────────────

  const stepIsCompleted = (n: number) => {
    if (n === 1) return selectedCategory !== null && activeStep > 1;
    if (n === 2) return selectedSource !== null && activeStep > 2;
    if (n === 3) return false;
    return false;
  };

  const stepIsLocked = (n: number) => n > activeStep;

  const stepLabel = (n: number) => {
    if (n === 1) return 'Source type';
    if (n === 2) return 'Source';
    if (n === 3) return selectedSourceNeedsSetup ? 'Configure' : 'Data model';
    return '';
  };

  const stepDescription = (n: number) => {
    if (n === 1) return 'Choose between subscription management or transaction-based billing';
    if (n === 2) return selectedCategory === 'subscription'
      ? 'Select your billing platform to connect'
      : 'Select how your transaction data will be ingested';
    if (n === 3) return selectedSourceNeedsSetup
      ? 'Enter your credentials to connect'
      : 'Choose the data structure that matches your revenue setup';
    return '';
  };

  const stepSummary = (n: number) => {
    if (n === 1 && selectedCategory) return CATEGORIES.find(c => c.id === selectedCategory)?.title ?? '';
    if (n === 2 && selectedSource) return selectedSource.name;
    if (n === 3) return DATA_MODEL_OPTIONS.find(o => o.value === selectedDataModel)?.label ?? '';
    return '';
  };

  // ── renderers ──────────────────────────────────────────────────────────────

  const renderCategoryContent = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        {CATEGORIES.map((cat) => {
          const Icon = cat.icon;
          const isSelected = selectedCategory === cat.id;
          return (
            <button
              key={cat.id}
              onClick={() => handleCategorySelect(cat.id)}
              className={`relative p-4 border-2 rounded-xl text-left transition-all ${
                isSelected ? 'border-primaryColor bg-primaryExtremeLightColor' : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              {isSelected && <SelectedCheck />}
              <div className="flex items-start gap-3">
                <div className={`p-2 rounded-lg shrink-0 ${isSelected ? 'bg-blue-100' : cat.iconBg}`}>
                  <Icon className={`w-5 h-5 ${cat.iconColor}`} />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 text-sm mb-1">{cat.title}</h4>
                  <p className="text-xs text-gray-500 leading-relaxed mb-2">{cat.description}</p>
                  <div className="flex flex-wrap gap-1">
                    {cat.tags.map((tag) => (
                      <span key={tag.id} title={tag.label} className="relative group inline-flex items-center justify-center w-6 h-6 rounded bg-gray-100 cursor-default">
                        {tag.icon}
                        <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 whitespace-nowrap rounded px-1.5 py-0.5 text-xs font-medium bg-gray-800 text-white opacity-0 group-hover:opacity-100 transition-opacity z-10">
                          {tag.label}
                        </span>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
      {selectedCategory === 'transactions' && hasSubscriptionConfigured && (
        <div className="flex items-start gap-2">
          <Info className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
          <p className="text-sm text-gray-600">
            This will only enable <span className="font-semibold">transaction and invoice tracking</span>. Subscription-level metrics from your existing integration will remain unaffected.
          </p>
        </div>
      )}

      {selectedCategory === 'transactions' && !hasSubscriptionConfigured && (
        <div className="flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-gray-800">No subscription management source connected</p>
            <p className="text-sm text-gray-600 mt-0.5">
              Without a subscription source (Chargebee or Stripe Billing), you will miss out on subscription-level metrics such as MRR, churn, and plan-level breakdowns.
            </p>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between pt-1">
        {!isDemoMode ? (
          <button
            onClick={() => toggleDemoMode('/analyze/dashboard')}
            className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
          >
            Skip setup, try demo mode →
          </button>
        ) : <span />}
        <button
          onClick={handleCategoryNext}
          disabled={!selectedCategory}
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
            selectedCategory ? 'bg-primaryColor text-white hover:bg-primaryDarkColor' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
          }`}
        >
          {selectedCategory === 'transactions' && !hasSubscriptionConfigured ? 'Proceed anyway' : 'Next'}
        </button>
      </div>
    </div>
  );

  const renderSourceContent = () => {
    const sources = sourcesForCategory;
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          {sources.map((source: any) => {
            const isAlreadyConfigured = alreadyConfiguredSourceIds.includes(source.id);
            const isDisabled = source.comingSoon || isAlreadyConfigured;
            const isSelected = selectedSource?.id === source.id;
            return (
              <button
                key={source.id}
                disabled={isDisabled}
                onClick={() => !isDisabled && handleSourceSelect({ id: source.id, name: source.name, category: selectedCategory! })}
                className={`relative p-4 border-2 rounded-xl text-left transition-all ${
                  isSelected
                    ? 'border-primaryColor bg-primaryExtremeLightColor'
                    : isDisabled
                    ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                {isSelected && <SelectedCheck />}
                {source.comingSoon && (
                  <span className="absolute top-3 right-3 inline-flex items-center gap-1 text-xs font-medium bg-yellow-50 text-yellow-700 px-2 py-0.5 rounded">
                    <Clock className="w-3 h-3" />
                    Coming soon
                  </span>
                )}
                {source.beta && !source.comingSoon && (
                  <span className="absolute top-3 right-3 text-xs font-medium bg-amber-50 text-amber-700 px-2 py-0.5 rounded">Beta</span>
                )}
                {isAlreadyConfigured && !source.comingSoon && (
                  <span className="absolute top-3 right-3 text-xs font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded">Configured</span>
                )}
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg shrink-0 ${isSelected ? 'bg-blue-100' : isDisabled ? 'bg-gray-100' : 'bg-white shadow-sm'}`}>
                    {source.logoType === 'chargebee' ? (
                      <img src={ChargebeeIcon} alt="Chargebee" className="w-5 h-5 object-contain" />
                    ) : source.logoType === 'stripe' || source.iconType === 'stripe' ? (
                      <SiStripe className={`w-5 h-5 ${source.comingSoon ? 'text-gray-300' : 'text-[#635BFF]'}`} />
                    ) : source.iconType === 'quickbooks' ? (
                      <SiQuickbooks className={`w-5 h-5 ${source.comingSoon ? 'text-gray-300' : 'text-[#2CA01C]'}`} />
                    ) : (
                      <source.icon className={`w-5 h-5 ${source.comingSoon ? 'text-gray-300' : source.iconColor}`} />
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className={`font-semibold text-sm mb-1 ${source.comingSoon ? 'text-gray-400' : 'text-gray-900'}`}>{source.name}</h3>
                    <p className={`text-xs mb-1.5 ${source.comingSoon ? 'text-gray-400' : 'text-gray-500'}`}>{source.description}</p>
                    <div className="space-y-0.5">
                      {source.features.map((f: string) => (
                        <div key={f} className="flex items-center gap-1.5">
                          <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${source.comingSoon ? 'bg-gray-300' : 'bg-blue-500'}`} />
                          <span className={`text-xs ${source.comingSoon ? 'text-gray-400' : 'text-gray-500'}`}>{f}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
        {selectedSourceNeedsDataModel && (
          <div className="flex justify-end pt-1">
            <button
              onClick={handleSourceNext}
              disabled={!selectedSource}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                selectedSource ? 'bg-primaryColor text-white hover:bg-primaryDarkColor' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
            >
              Next
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderDataModelContent = () => (
    <div className="space-y-4">
      <div className="space-y-2">
        {DATA_MODEL_OPTIONS.map((option) => {
          const Icon = option.icon;
          const isSelected = selectedDataModel === option.value;
          return (
            <button
              key={option.value}
              disabled={option.disabled}
              onClick={() => {
                if (option.disabled || !selectedSource) return;
                setSelectedDataModel(option.value);
                commitSelection(selectedSource, option.value);
              }}
              className={`relative w-full p-4 border-2 rounded-xl text-left transition-all ${
                isSelected
                  ? 'border-primaryColor bg-primaryExtremeLightColor'
                  : option.disabled
                  ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-50'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              {option.disabled && (
                <span className="absolute top-3 right-3 text-xs font-medium bg-gray-100 text-gray-500 px-2 py-0.5 rounded">Coming soon</span>
              )}
              {isSelected && <SelectedCheck />}
              <div className="flex items-start gap-3">
                <div className={`p-2 rounded-lg shrink-0 ${isSelected ? 'bg-blue-100' : option.disabled ? 'bg-gray-100' : 'bg-white shadow-sm'}`}>
                  <Icon className={`w-5 h-5 ${option.disabled ? 'text-gray-400' : option.iconColor}`} />
                </div>
                <div className="flex-1">
                  <p className={`font-semibold text-sm ${option.disabled ? 'text-gray-400' : 'text-gray-900'}`}>{option.label}</p>
                  <p className={`text-xs mt-0.5 ${option.disabled ? 'text-gray-400' : 'text-gray-500'}`}>{option.description}</p>
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {option.entities.map((e) => (
                      <span key={e} className={`text-xs px-1.5 py-0.5 rounded font-mono ${option.disabled ? 'bg-gray-100 text-gray-400' : isSelected ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>{e}</span>
                    ))}
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );

  const renderStepContent = (n: number) => {
    if (n === 1) return renderCategoryContent();
    if (n === 2) return renderSourceContent();
    if (n === 3) return renderDataModelContent();
    return null;
  };

  // ── stepper shell ──────────────────────────────────────────────────────────

  const stepsToShow = selectedSourceNeedsDataModel || activeStep >= 3 ? [1, 2, 3] : [1, 2];

  return (
    <div className="space-y-0 relative">
      {/* vertical connector line */}
      <div className="absolute left-[18px] top-8 bottom-8 w-px bg-gray-200 z-0" />

      {stepsToShow.map((n) => {
        const completed = stepIsCompleted(n);
        const locked = stepIsLocked(n);
        const active = activeStep === n;

        return (
          <div key={n} className="relative z-10">
            <div
              className={`flex items-center gap-3 px-1 py-3 cursor-pointer select-none ${locked ? 'opacity-40 cursor-not-allowed' : ''}`}
              onClick={() => {
                if (locked) return;
                if (completed) setActiveStep(n);
              }}
            >
              {/* circle */}
              <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 border-2 text-sm font-bold z-10 ${
                completed
                  ? 'bg-green-500 border-green-500 text-white'
                  : active
                  ? 'bg-primaryColor border-primaryColor text-white'
                  : 'bg-white border-gray-300 text-gray-400'
              }`}>
                {completed ? <CheckCircle className="w-4 h-4" /> : n}
              </div>

              <div className="flex-1 flex items-center justify-between">
                <div>
                  <p className={`text-sm font-bold ${locked ? 'text-gray-400' : 'text-gray-900'}`}>{stepLabel(n)}</p>
                  {completed ? (
                    <p className="text-xs text-green-700 font-medium mt-0.5">{stepSummary(n)}</p>
                  ) : (
                    <p className={`text-xs mt-0.5 ${locked ? 'text-gray-300' : 'text-gray-500'}`}>{stepDescription(n)}</p>
                  )}
                </div>
                {completed && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setActiveStep(n); setSelectedSource(null); }}
                    className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                )}
                {!completed && (
                  <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${active ? 'rotate-180' : ''}`} />
                )}
              </div>
            </div>

            {active && (
              <div className="ml-12 pb-4">
                {renderStepContent(n)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
